import { useState, useEffect, useMemo, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Calendar, Clock, Users, User as UserIcon, Tag, ExternalLink,
  Plus, Pencil, Trash2, X, PlayCircle, Ticket, Sparkles, Upload, ImageIcon
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PLACEHOLDER_IMG = "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&h=450&fit=crop";

const EVENT_TYPES = ["Networking", "Webinaire", "Workshop"];
const EVENT_TAGS = [
  "Acquisition", "Entrepreneuriat", "Finance", "IA", "Industrie",
  "Leadership", "Management", "Marketing", "Productivité", "Vente",
];

function formatDateLong(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

function toDatetimeLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function EventCard({ event, isPast, isAdmin, onEdit, onDelete }) {
  const navigate = useNavigate();
  const rawImg = event.image_url || PLACEHOLDER_IMG;
  const img = rawImg.startsWith("/api/uploads/") ? `${API}${rawImg}` : rawImg;
  return (
    <div className="cbm-card overflow-hidden flex flex-col h-full" data-testid={`event-${event._id}`}>
      {/* Image */}
      <div className="relative aspect-video overflow-hidden" style={{ background: "#071028" }}>
        <img
          src={img}
          alt={event.title}
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-[1.03]"
          onError={(e) => { e.target.src = PLACEHOLDER_IMG; }}
        />
        {isPast && (
          <div className="absolute inset-0 bg-navy/40" />
        )}
        {/* Tag + past badge */}
        <div className="absolute top-3 left-3 flex gap-1.5 flex-wrap">
          {event.theme_tag && (
            <span className="cbm-badge cbm-badge-verified backdrop-blur" style={{ background: "rgba(11,24,54,0.7)" }}>
              <Tag size={9} className="mr-0.5" />{event.theme_tag}
            </span>
          )}
          {isPast && (
            <span className="cbm-badge cbm-badge-closed backdrop-blur" style={{ background: "rgba(11,24,54,0.7)" }}>
              Replay
            </span>
          )}
        </div>
        {/* Admin controls */}
        {isAdmin && (
          <div className="absolute top-3 right-3 flex gap-1">
            <button
              data-testid={`edit-event-${event._id}`}
              onClick={() => onEdit(event)}
              className="w-7 h-7 rounded-md flex items-center justify-center bg-navy/80 hover:bg-gold/20 border border-white/10 hover:border-gold/40 transition-all backdrop-blur"
              title="Modifier"
            >
              <Pencil size={12} className="text-cbm-secondary" />
            </button>
            <button
              data-testid={`delete-event-${event._id}`}
              onClick={() => onDelete(event)}
              className="w-7 h-7 rounded-md flex items-center justify-center bg-navy/80 hover:bg-red-900/30 border border-white/10 hover:border-red-500/40 transition-all backdrop-blur"
              title="Supprimer"
            >
              <Trash2 size={12} className="text-red-400" />
            </button>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="p-5 flex flex-col flex-1">
        {event.event_type && (
          <p className="cbm-label mb-1 text-gold/80" data-testid={`event-type-${event._id}`}>
            {event.event_type}
          </p>
        )}
        <h3 className="font-outfit font-semibold text-white text-base leading-snug mb-2">{event.title}</h3>
        <p className="text-cbm-secondary text-sm leading-relaxed line-clamp-3 mb-3 flex-1">{event.description}</p>

        {/* Meta */}
        <div className="space-y-1.5 text-xs mb-4">
          <div className="flex items-center gap-2 text-cbm-secondary">
            <Calendar size={13} className="text-gold flex-shrink-0" />
            <span className="capitalize">{formatDateLong(event.event_date)}</span>
          </div>
          <div className="flex items-center gap-2 text-cbm-secondary">
            <Clock size={13} className="text-gold flex-shrink-0" />
            <span>{formatTime(event.event_date)}</span>
          </div>
          {event.speaker && (
            <div className="flex items-center gap-2 text-cbm-secondary">
              <UserIcon size={13} className="text-gold flex-shrink-0" />
              <span className="truncate">{event.speaker}</span>
            </div>
          )}
          {!isPast && event.limited_places && (
            <div className="flex items-center gap-2 text-cbm-muted">
              <Users size={13} className="flex-shrink-0" />
              <span>{event.limited_places} places limitées</span>
            </div>
          )}
        </div>

        {/* CTA */}
        {isPast ? (
          event.replay_url ? (
            <button
              data-testid={`replay-button-${event._id}`}
              onClick={() => navigate(`/events/${event._id}`)}
              className="cbm-btn-secondary text-sm w-full justify-center"
            >
              <PlayCircle size={14} /> Voir le replay
            </button>
          ) : (
            <button
              data-testid={`view-event-button-${event._id}`}
              onClick={() => navigate(`/events/${event._id}`)}
              className="cbm-btn-ghost text-sm w-full justify-center border border-white/10 hover:border-white/25"
            >
              <ExternalLink size={14} /> Voir les détails
            </button>
          )
        ) : (
          event.registration_url ? (
            <a
              data-testid={`register-button-${event._id}`}
              href={event.registration_url}
              target="_blank"
              rel="noopener noreferrer"
              className="cbm-btn-primary text-sm w-full justify-center"
            >
              <Ticket size={14} /> Participer <ExternalLink size={11} />
            </a>
          ) : (
            <div className="cbm-btn-ghost text-sm w-full justify-center cursor-default opacity-50 border border-white/10">
              Inscription à venir
            </div>
          )
        )}
      </div>
    </div>
  );
}

function EventFormModal({ event, onClose, onSaved }) {
  const isEdit = !!event?._id;
  const [form, setForm] = useState({
    title: event?.title || "",
    description: event?.description || "",
    image_url: event?.image_url || "",
    event_type: event?.event_type || EVENT_TYPES[0],
    theme_tag: event?.theme_tag || EVENT_TAGS[0],
    speaker: event?.speaker || "",
    speaker_profile_id: event?.speaker_profile_id || "",
    event_date: event ? toDatetimeLocal(event.event_date) : "",
    limited_places: event?.limited_places || "",
    registration_url: event?.registration_url || "",
    replay_url: event?.replay_url || "",
    show_contact_speaker: event?.show_contact_speaker || false,
    show_post_opportunity: event?.show_post_opportunity || false,
    show_view_directory: event?.show_view_directory || false,
    status: event?.status || "upcoming",
  });
  const [resources, setResources] = useState(
    (event?.resources && event.resources.length > 0) ? event.resources : []
  );
  const [profiles, setProfiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState(
    event?.image_url && event.image_url.startsWith("/api/uploads/") ? event.image_url : ""
  );
  const [urlInput, setUrlInput] = useState(
    event?.image_url && !event.image_url.startsWith("/api/uploads/") ? event.image_url : ""
  );
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Load members list for speaker selector
  useEffect(() => {
    axios.get(`${API}/api/profiles?limit=500`, { withCredentials: true })
      .then(({ data }) => setProfiles(data.items || []))
      .catch(() => setProfiles([]));
  }, []);

  // Effective preview: uploaded takes priority over URL input
  const previewUrl = uploadedUrl
    ? (uploadedUrl.startsWith("http") ? uploadedUrl : `${API}${uploadedUrl}`)
    : urlInput;

  const handleFile = async (file) => {
    if (!file) return;
    const validExt = /\.(jpe?g|png|webp)$/i.test(file.name);
    if (!validExt) { setError("Format non supporté (jpg, png, webp)"); return; }
    if (file.size > 5 * 1024 * 1024) { setError("Fichier trop volumineux (max 5MB)"); return; }
    setError("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/api/upload/photo`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadedUrl(data.url);
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'upload");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  const removeImage = () => {
    setUploadedUrl("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.title.trim() || !form.description.trim() || !form.event_date) {
      setError("Titre, description et date sont requis");
      return;
    }
    setSubmitting(true);
    try {
      // Uploaded image has priority over manually entered URL
      const finalImageUrl = uploadedUrl || urlInput || "";
      // If a speaker profile is linked and no speaker name was set, enrich from profile
      let speakerName = form.speaker;
      if (form.speaker_profile_id && !speakerName) {
        const p = profiles.find(x => x._id === form.speaker_profile_id);
        if (p) speakerName = p.nom_prenom;
      }
      const payload = {
        ...form,
        speaker: speakerName,
        image_url: finalImageUrl,
        event_date: new Date(form.event_date).toISOString(),
        limited_places: form.limited_places ? parseInt(form.limited_places, 10) : null,
        resources: resources.filter(r => r.title.trim() && r.url.trim()),
      };
      if (isEdit) {
        await axios.patch(`${API}/api/admin/events/${event._id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/api/admin/events`, payload, { withCredentials: true });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto animate-fade-in"
      onClick={onClose}
    >
      <div
        className="cbm-card my-6 w-full max-w-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="event-form-modal"
      >
        <div className="flex items-center justify-between p-5 border-b cbm-divider">
          <h2 className="font-outfit font-semibold text-white text-lg">
            {isEdit ? "Modifier l'événement" : "Créer un événement"}
          </h2>
          <button
            data-testid="close-event-form"
            onClick={onClose}
            className="cbm-btn-ghost text-cbm-muted hover:text-white p-1"
          >
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          {/* Status toggle */}
          <div>
            <label className="cbm-label block mb-2">Statut de l'événement</label>
            <div className="grid grid-cols-2 gap-2" data-testid="event-status-toggle">
              {[
                { value: "upcoming", label: "À venir", hint: "Inscription active" },
                { value: "past", label: "Terminé (Replay)", hint: "Vidéo + ressources + CTA" },
              ].map(s => (
                <button
                  key={s.value}
                  type="button"
                  data-testid={`status-${s.value}`}
                  onClick={() => setForm(p => ({ ...p, status: s.value }))}
                  className={`text-left p-3 rounded-lg border transition-all ${
                    form.status === s.value
                      ? "border-gold bg-gold/10"
                      : "border-white/10 hover:border-white/25 bg-transparent"
                  }`}
                >
                  <p className={`font-outfit font-semibold text-sm ${form.status === s.value ? "text-gold" : "text-white"}`}>{s.label}</p>
                  <p className="text-cbm-muted text-xs mt-0.5">{s.hint}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="cbm-label block mb-1.5">Titre *</label>
              <input
                data-testid="event-title-input"
                className="cbm-input"
                placeholder="Soirée networking CBM"
                value={form.title}
                maxLength={150}
                onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))}
              />
            </div>
            <div className="sm:col-span-2">
              <label className="cbm-label block mb-1.5">Description *</label>
              <textarea
                data-testid="event-description-input"
                className="cbm-input"
                rows={3}
                placeholder="Description courte de l'événement..."
                value={form.description}
                maxLength={800}
                onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
              />
            </div>
            <div className="sm:col-span-2">
              <label className="cbm-label block mb-1.5">Photo de couverture</label>

              {/* Upload / drag-drop zone */}
              {previewUrl ? (
                <div className="relative rounded-xl overflow-hidden border border-white/10 group" data-testid="image-preview-wrapper">
                  <img
                    src={previewUrl}
                    alt="Aperçu"
                    className="w-full aspect-video object-cover block"
                    onError={(e) => { e.target.style.opacity = "0.3"; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute top-2 right-2 flex gap-1.5">
                    <button
                      type="button"
                      data-testid="replace-image-button"
                      onClick={() => fileInputRef.current?.click()}
                      className="px-2.5 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 backdrop-blur border border-white/20 text-white hover:bg-white/15"
                      style={{ background: "rgba(11,24,54,0.75)" }}
                    >
                      <Upload size={12} /> Remplacer
                    </button>
                    <button
                      type="button"
                      data-testid="remove-image-button"
                      onClick={removeImage}
                      className="px-2.5 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 backdrop-blur border border-red-400/30 text-red-300 hover:bg-red-900/25"
                      style={{ background: "rgba(11,24,54,0.75)" }}
                    >
                      <Trash2 size={12} /> Supprimer
                    </button>
                  </div>
                  {uploadedUrl && (
                    <span
                      className="absolute bottom-2 left-2 px-2 py-1 rounded text-[10px] font-medium text-white backdrop-blur"
                      style={{ background: "rgba(11,24,54,0.75)", border: "1px solid rgba(201,162,39,0.35)" }}
                    >
                      Upload
                    </span>
                  )}
                </div>
              ) : (
                <div
                  data-testid="image-dropzone"
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={onDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`rounded-xl border-2 border-dashed cursor-pointer transition-all flex flex-col items-center justify-center aspect-[3/1] p-4 text-center ${
                    dragOver ? "border-gold/60 bg-gold/5" : "border-white/15 hover:border-white/30 bg-white/[0.02]"
                  }`}
                >
                  {uploading ? (
                    <>
                      <div className="w-6 h-6 border-2 border-gold border-t-transparent rounded-full animate-spin mb-2" />
                      <p className="text-cbm-secondary text-sm">Upload en cours...</p>
                    </>
                  ) : (
                    <>
                      <ImageIcon size={26} className="text-cbm-muted mb-2" />
                      <p className="text-white text-sm font-medium mb-0.5">Ajouter une photo de couverture</p>
                      <p className="text-cbm-muted text-xs">Glissez-déposez ou cliquez · JPG, PNG, WEBP · 5 MB max</p>
                    </>
                  )}
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                data-testid="image-file-input"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />

              {/* URL fallback — only shown if no uploaded image */}
              {!uploadedUrl && (
                <div className="mt-3">
                  <label className="text-cbm-muted text-xs block mb-1">Ou URL externe (optionnel)</label>
                  <input
                    data-testid="event-image-input"
                    className="cbm-input"
                    placeholder="https://..."
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                  />
                </div>
              )}
            </div>
            <div>
              <label className="cbm-label block mb-1.5">Type *</label>
              <select
                data-testid="event-type-input"
                className="cbm-input"
                value={form.event_type}
                onChange={(e) => setForm(p => ({ ...p, event_type: e.target.value }))}
              >
                {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="cbm-label block mb-1.5">Tag *</label>
              <select
                data-testid="event-theme-input"
                className="cbm-input"
                value={form.theme_tag}
                onChange={(e) => setForm(p => ({ ...p, theme_tag: e.target.value }))}
              >
                {EVENT_TAGS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="cbm-label block mb-1.5">Intervenant</label>
              <input
                data-testid="event-speaker-input"
                className="cbm-input"
                placeholder="Nom de l'intervenant"
                value={form.speaker}
                maxLength={150}
                onChange={(e) => setForm(p => ({ ...p, speaker: e.target.value }))}
              />
            </div>
            <div>
              <label className="cbm-label block mb-1.5">Date et heure *</label>
              <input
                data-testid="event-date-input"
                type="datetime-local"
                className="cbm-input"
                value={form.event_date}
                onChange={(e) => setForm(p => ({ ...p, event_date: e.target.value }))}
              />
            </div>
            <div>
              <label className="cbm-label block mb-1.5">Places limitées (optionnel)</label>
              <input
                data-testid="event-places-input"
                type="number"
                min="1"
                className="cbm-input"
                placeholder="30"
                value={form.limited_places}
                onChange={(e) => setForm(p => ({ ...p, limited_places: e.target.value }))}
              />
            </div>
            <div className="sm:col-span-2">
              <label className="cbm-label block mb-1.5">Lien d'inscription (Tally)</label>
              <input
                data-testid="event-registration-input"
                className="cbm-input"
                placeholder="https://tally.so/r/..."
                value={form.registration_url}
                onChange={(e) => setForm(p => ({ ...p, registration_url: e.target.value }))}
              />
            </div>

            {/* Speaker profile link (available for both statuses) */}
            <div className="sm:col-span-2">
              <label className="cbm-label block mb-1.5">Profil intervenant (optionnel)</label>
              <select
                data-testid="event-speaker-profile-input"
                className="cbm-input"
                value={form.speaker_profile_id}
                onChange={(e) => setForm(p => ({ ...p, speaker_profile_id: e.target.value }))}
              >
                <option value="">— Aucun profil lié —</option>
                {profiles.map(p => (
                  <option key={p._id} value={p._id}>
                    {p.nom_prenom}{p.entreprise ? ` · ${p.entreprise}` : ""}
                  </option>
                ))}
              </select>
              <p className="text-cbm-muted text-xs mt-1">Active le bouton "Voir profil" et "Contacter l'intervenant" sur la page replay.</p>
            </div>

            {form.status === "past" && (
              <>
                <div className="sm:col-span-2">
                  <label className="cbm-label block mb-1.5">Lien replay (optionnel)</label>
                  <input
                    data-testid="event-replay-input"
                    className="cbm-input"
                    placeholder="https://youtu.be/... · https://vimeo.com/... · https://zoom.us/rec/..."
                    value={form.replay_url}
                    onChange={(e) => setForm(p => ({ ...p, replay_url: e.target.value }))}
                  />
                  <p className="text-cbm-muted text-xs mt-1">YouTube / Vimeo / Loom seront intégrés automatiquement.</p>
                </div>

                {/* Resources */}
                <div className="sm:col-span-2">
                  <div className="flex items-center justify-between mb-2">
                    <label className="cbm-label">Ressources (optionnel)</label>
                    <button
                      type="button"
                      data-testid="add-resource-button"
                      className="cbm-btn-ghost text-xs py-1 px-2 border border-white/10 hover:border-gold/40 hover:text-gold"
                      onClick={() => setResources(r => [...r, { title: "", url: "" }])}
                    >
                      <Plus size={12} /> Ajouter une ressource
                    </button>
                  </div>
                  {resources.length === 0 ? (
                    <p className="text-cbm-muted text-xs">Ajoutez des liens vers des slides, documents, outils partagés...</p>
                  ) : (
                    <div className="space-y-2">
                      {resources.map((r, idx) => (
                        <div key={idx} className="flex items-center gap-2" data-testid={`resource-row-${idx}`}>
                          <input
                            data-testid={`resource-title-${idx}`}
                            className="cbm-input flex-1"
                            placeholder="Titre (ex: Slides de la session)"
                            value={r.title}
                            onChange={(e) => setResources(rs => rs.map((x, i) => i === idx ? { ...x, title: e.target.value } : x))}
                          />
                          <input
                            data-testid={`resource-url-${idx}`}
                            className="cbm-input flex-1"
                            placeholder="https://..."
                            value={r.url}
                            onChange={(e) => setResources(rs => rs.map((x, i) => i === idx ? { ...x, url: e.target.value } : x))}
                          />
                          <button
                            type="button"
                            data-testid={`remove-resource-${idx}`}
                            onClick={() => setResources(rs => rs.filter((_, i) => i !== idx))}
                            className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 border border-red-900/30 text-red-400 hover:bg-red-900/10"
                            title="Supprimer"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* CTA toggles */}
                <div className="sm:col-span-2">
                  <label className="cbm-label block mb-2">CTA sur la page replay</label>
                  <div className="grid grid-cols-1 gap-2">
                    {[
                      { key: "show_contact_speaker", label: "Afficher « Contacter l'intervenant »", hint: "Requiert un profil intervenant lié" },
                      { key: "show_post_opportunity", label: "Afficher « Poster une annonce »", hint: "Redirige vers /poster-annonce" },
                      { key: "show_view_directory", label: "Afficher « Voir l'annuaire »", hint: "Redirige vers /annuaire" },
                    ].map(t => (
                      <label
                        key={t.key}
                        className="flex items-start gap-3 p-3 rounded-lg border border-white/10 hover:border-white/25 cursor-pointer transition-all"
                      >
                        <input
                          type="checkbox"
                          data-testid={`toggle-${t.key}`}
                          checked={!!form[t.key]}
                          onChange={(e) => setForm(p => ({ ...p, [t.key]: e.target.checked }))}
                          className="mt-0.5 w-4 h-4 accent-gold cursor-pointer"
                        />
                        <div className="flex-1">
                          <p className="text-white text-sm font-medium">{t.label}</p>
                          <p className="text-cbm-muted text-xs mt-0.5">{t.hint}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
          {error && <p className="text-red-400 text-sm" data-testid="event-form-error">{error}</p>}
          <div className="flex items-center justify-end gap-2 pt-3 border-t cbm-divider">
            <button type="button" className="cbm-btn-ghost text-sm border border-white/10" onClick={onClose}>
              Annuler
            </button>
            <button
              data-testid="submit-event-button"
              type="submit"
              className="cbm-btn-primary text-sm"
              disabled={submitting}
            >
              {submitting ? "Enregistrement..." : (isEdit ? "Mettre à jour" : "Créer l'événement")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Evenements() {
  const { user } = useAuth();
  const location = useLocation();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState({ upcoming: [], past: [] });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [typeFilter, setTypeFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/events`, { withCredentials: true });
      setData(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Scroll to specific event card if hash is present: #event-{id}
  useEffect(() => {
    if (loading) return;
    const hash = location.hash;
    if (!hash || !hash.startsWith("#event-")) return;
    const el = document.getElementById(hash.slice(1));
    if (el) {
      setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-gold/60");
        setTimeout(() => el.classList.remove("ring-2", "ring-gold/60"), 2400);
      }, 200);
    }
  }, [loading, data, location.hash]);

  const openCreate = () => { setEditing(null); setShowForm(true); };
  const openEdit = (ev) => { setEditing(ev); setShowForm(true); };

  const onDelete = async (ev) => {
    if (!window.confirm(`Supprimer l'événement "${ev.title}" ?`)) return;
    try {
      await axios.delete(`${API}/api/admin/events/${ev._id}`, { withCredentials: true });
      load();
    } catch {
      alert("Erreur lors de la suppression");
    }
  };

  const { upcoming, past } = data;

  const applyFilters = (list) => list.filter(ev => {
    if (typeFilter && ev.event_type !== typeFilter) return false;
    if (tagFilter && ev.theme_tag !== tagFilter) return false;
    return true;
  });

  const filteredUpcoming = applyFilters(upcoming);
  const filteredPast = applyFilters(past);

  return (
    <div className="p-6 max-w-7xl mx-auto animate-fade-in">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "rgba(201,162,39,0.15)", border: "1px solid rgba(201,162,39,0.3)" }}>
              <Calendar size={18} className="text-gold" />
            </div>
            <h1 className="cbm-section-title">Événements</h1>
          </div>
          <p className="text-cbm-muted text-sm ml-[52px]">Rendez-vous, conférences et sessions networking du club</p>
        </div>
        {isAdmin && (
          <button
            data-testid="create-event-button"
            className="cbm-btn-primary text-sm"
            onClick={openCreate}
          >
            <Plus size={15} /> Créer un événement
          </button>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[1, 2, 3].map(i => <div key={i} className="cbm-skeleton rounded-xl" style={{ height: 380 }} />)}
        </div>
      ) : (
        <>
          {/* Filters */}
          <div className="cbm-filter-bar mb-6 animate-fade-in-up" data-testid="events-filters">
            <div className="flex flex-col sm:flex-row gap-3">
              <select
                data-testid="filter-event-type"
                className="cbm-input sm:w-48"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="">Tous les types</option>
                {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <select
                data-testid="filter-event-tag"
                className="cbm-input sm:w-56"
                value={tagFilter}
                onChange={(e) => setTagFilter(e.target.value)}
              >
                <option value="">Tous les thèmes</option>
                {EVENT_TAGS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              {(typeFilter || tagFilter) && (
                <button
                  className="cbm-btn-ghost text-sm border border-white/10"
                  onClick={() => { setTypeFilter(""); setTagFilter(""); }}
                  data-testid="clear-event-filters"
                >
                  <X size={14} /> Réinitialiser
                </button>
              )}
            </div>
          </div>

          {/* Upcoming */}
          <section className="mb-10" data-testid="events-upcoming-section">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles size={15} className="text-gold" />
              <h2 className="font-outfit font-semibold text-white text-base">À venir</h2>
              <span className="text-cbm-muted text-sm">({filteredUpcoming.length})</span>
            </div>
            {filteredUpcoming.length === 0 ? (
              <div className="cbm-card p-10 text-center">
                <Calendar size={36} className="text-cbm-muted mx-auto mb-3 opacity-60" />
                <p className="text-cbm-secondary text-sm">
                  {typeFilter || tagFilter ? "Aucun événement ne correspond aux filtres." : "Aucun événement à venir pour le moment."}
                </p>
                {isAdmin && !typeFilter && !tagFilter && (
                  <button
                    className="cbm-btn-secondary text-sm mt-4"
                    onClick={openCreate}
                    data-testid="create-event-empty"
                  >
                    <Plus size={13} /> Créer le premier événement
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="events-upcoming-grid">
                {filteredUpcoming.map((ev, idx) => (
                  <div
                    key={ev._id}
                    id={`event-${ev._id}`}
                    className={`rounded-xl transition-shadow animate-fade-in-up-delay-${Math.min(idx + 1, 6)}`}
                  >
                    <EventCard event={ev} isPast={false} isAdmin={isAdmin} onEdit={openEdit} onDelete={onDelete} />
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Past */}
          <section data-testid="events-past-section">
            <div className="flex items-center gap-2 mb-4">
              <PlayCircle size={15} className="text-cbm-muted" />
              <h2 className="font-outfit font-semibold text-white text-base">Événements passés</h2>
              <span className="text-cbm-muted text-sm">({filteredPast.length})</span>
            </div>
            {filteredPast.length === 0 ? (
              <p className="text-cbm-muted text-sm text-center py-6">
                {typeFilter || tagFilter ? "Aucun événement passé ne correspond aux filtres." : "Aucun événement passé."}
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="events-past-grid">
                {filteredPast.map((ev, idx) => (
                  <div
                    key={ev._id}
                    id={`event-${ev._id}`}
                    className={`rounded-xl transition-shadow animate-fade-in-up-delay-${Math.min(idx + 1, 6)}`}
                  >
                    <EventCard event={ev} isPast={true} isAdmin={isAdmin} onEdit={openEdit} onDelete={onDelete} />
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {showForm && (
        <EventFormModal
          event={editing}
          onClose={() => setShowForm(false)}
          onSaved={load}
        />
      )}
    </div>
  );
}
